
import React, { useState, useRef, useEffect } from 'react';
import { useToasts } from './TactileToasts';
import { UserPersona } from '../types/synclothe';
import DNAInfo from './DNAInfo';

interface IdentityVaultProps {
  onComplete: (description: string) => void;
  onSkip: () => void;
  existingPersona?: UserPersona;
  initialStep?: 'capture' | 'review';
}

const IdentityVault: React.FC<IdentityVaultProps> = ({ onComplete, onSkip, existingPersona, initialStep = 'capture' }) => {
  const { showToast } = useToasts();
  const [step, setStep] = useState<'capture' | 'review'>(initialStep);
  const [isReadOnly, setIsReadOnly] = useState<boolean>(initialStep === 'review' && !!existingPersona?.description);
  const [isProcessing, setIsProcessing] = useState(false);
  const [preview, setFilePreview] = useState<string | null>(null);
  const [showCamera, setShowCamera] = useState(false);
  const [biometricConsent, setBiometricConsent] = useState(false);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  
  const [dna, setDna] = useState<Partial<UserPersona>>(existingPersona || {});
  const [twinUrl, setTwinUrl] = useState<string | null>(existingPersona?.anchorUrl || null);

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const startCamera = async () => {
    if (!biometricConsent) {
      showToast("Affirmative biometric consent required prior to activating camera.", "error");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' } });
      streamRef.current = stream;
      setShowCamera(true);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      showToast("Webcam access denied.", "error");
      setShowCamera(false);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject as MediaStream;
      stream.getTracks().forEach(track => track.stop());
      videoRef.current.srcObject = null;
    }
    setShowCamera(false);
  };

  // Bind active MediaStream once the <video> element mounts inside the camera modal
  useEffect(() => {
    if (showCamera && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play().catch(() => {});
    }
  }, [showCamera]);

  // Hardware Conservation: Stop webcam stream when user leaves tab
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        stopCamera();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  // Platform Adaptive Detection: On mobile/iOS, native input sheet provides superior camera capture
  useEffect(() => {
    const checkMobile = () => {
      if (typeof window === 'undefined' || typeof navigator === 'undefined') return;
      const mobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || 
                     (navigator.maxTouchPoints > 1 && /Macintosh/i.test(navigator.userAgent));
      setIsMobile(mobile);
    };
    checkMobile();
  }, []);

  const analyzeImage = async (imgDataUrl: string) => {
    if (!biometricConsent) {
      showToast("Affirmative biometric consent required.", "error");
      return;
    }
    setIsProcessing(true);
    showToast("Decoding physical DNA markers...", "logic");

    try {
      const base64Data = imgDataUrl.split(',')[1];
      const contentType = imgDataUrl.split(';')[0].split(':')[1];

      const response = await fetch('/api/analyzePersona', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ imageData: base64Data, contentType })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Analysis failed");

      setDna(data.dna);
      setIsReadOnly(false);
      setStep('review');
      showToast("DNA Decoded. Review and calibrate your profile.", "success");
    } catch (err: any) {
      showToast(err.message, "error");
    } finally {
      setIsProcessing(false);
    }
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx?.drawImage(video, 0, 0);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
      setFilePreview(dataUrl);
      stopCamera();
      analyzeImage(dataUrl);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!biometricConsent) {
      showToast("Affirmative biometric consent required prior to portrait upload.", "error");
      e.target.value = '';
      return;
    }
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const rawData = reader.result as string;
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          const maxDim = 1200;
          if (width > maxDim || height > maxDim) {
            if (width > height) {
              height = Math.round((height * maxDim) / width);
              width = maxDim;
            } else {
              width = Math.round((width * maxDim) / height);
              height = maxDim;
            }
          }
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx?.drawImage(img, 0, 0, width, height);
          const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
          setFilePreview(dataUrl);
          analyzeImage(dataUrl);
        };
        img.onerror = () => {
          setFilePreview(rawData);
          analyzeImage(rawData);
        };
        img.src = rawData;
      };
      reader.readAsDataURL(file);
    }
  };

  const handleInitialize = async () => {
    if (!preview) return;
    analyzeImage(preview);
  };

  const handleFinalize = async () => {
    setIsProcessing(true);
    showToast("Synthesizing Digital Twin & Locking Identity...", "sync");

    try {
      let base64Data: string | undefined;
      let contentType: string | undefined;
      if (preview) {
        base64Data = preview.split(',')[1];
        contentType = preview.split(';')[0].split(':')[1];
      }

      const response = await fetch('/api/savePersona', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          dna, 
          anchorUrl: twinUrl || undefined,
          imageData: base64Data,
          contentType: contentType 
        })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || "Sync failed");
      
      if (data.persona?.anchorUrl) {
        setTwinUrl(data.persona.anchorUrl);
      }
      if (data.persona) {
        setDna(data.persona);
      }
      // Purge uploaded reference image immediately post-synthesis
      setFilePreview(null);
      setIsReadOnly(true);
      showToast("Vault Secure. Identity Locked.", "success");
      onComplete(data.persona?.description || dna.description || "");
    } catch (err: any) {
      showToast(err.message, "error");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRecalibrate = () => {
    setFilePreview(null);
    setIsReadOnly(false);
    setStep('capture');
  };

  return (
    <div className="fixed inset-0 z-[200] bg-dk-900 overflow-y-auto animate-fade-in">
      <div className="min-h-full flex items-center justify-center p-4 md:p-8">
        <div className="glass-panel max-w-4xl w-full p-8 md:p-12 rounded-[2.5rem] border-azure/20 shadow-2xl relative overflow-hidden">
          
          <button 
              onClick={onSkip} 
              className="absolute top-6 right-6 md:top-8 md:right-8 text-gray-500 hover:text-white transition-colors z-50 p-2 bg-white/5 hover:bg-white/10 rounded-full"
          >
              <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
          </button>

          {step === 'capture' ? (
          <div className="flex flex-col gap-8">
            <header className="text-center">
                <span className="text-azure-400 text-[10px] font-black uppercase tracking-[0.5em] mb-4 block text-glow-azure">Identity Protocol: Layer 01</span>
                <h2 className="text-2xl md:text-4xl font-black text-white italic uppercase tracking-tighter leading-none mb-4">Initialize <span className="text-azure-500">Digital Twin</span></h2>
                <p className="text-gray-500 text-[10px] md:text-xs font-bold uppercase tracking-[0.2em] leading-relaxed max-w-md mx-auto">
                    Transient photo capture for DNA extraction. Your raw image is purged immediately after processing.
                </p>
            </header>

            <div className="flex flex-col gap-6 items-center">
                {preview ? (
                    <div className="relative group">
                        <img src={preview} alt="Identity Anchor" className="w-48 h-48 md:w-64 md:h-64 object-cover rounded-[2rem] border-2 border-azure/30 shadow-2xl shadow-azure/20" />
                        <button onClick={() => setFilePreview(null)} className="absolute -top-3 -right-3 bg-red-500 text-white p-2 rounded-full shadow-lg hover:scale-110 transition-transform">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M6 18L18 6M6 6l12 12"></path></svg>
                        </button>
                    </div>
                ) : (
                    <div className="w-48 h-48 md:w-64 md:h-64 border-2 border-dashed border-white/10 rounded-[2rem] flex flex-col items-center justify-center gap-4 bg-white/[0.02]">
                        <div className="w-12 h-12 rounded-full bg-azure/10 flex items-center justify-center text-azure-400">
                            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20"><path d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"></path></svg>
                        </div>
                        <span className="text-[10px] font-black uppercase tracking-widest text-gray-600">Secure Capture Node</span>
                    </div>
                )}

                {!preview && (
                    <div className="w-full max-w-sm p-4 rounded-2xl bg-[#0f172a]/80 border border-azure-500/30 text-left space-y-2 shadow-lg">
                        <div className="flex items-center justify-between">
                            <span className="text-[9px] font-black uppercase tracking-widest text-gold flex items-center gap-1.5">
                                <svg className="w-3 h-3 text-gold" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path></svg>
                                Biometric Privacy Opt-In
                            </span>
                            <span className="text-[8px] font-mono font-bold text-gray-400 uppercase">GDPR Art. 9 & BIPA</span>
                        </div>
                        <label className="flex items-start gap-3 cursor-pointer select-none">
                            <input
                                type="checkbox"
                                checked={biometricConsent}
                                onChange={(e) => {
                                    const checked = e.target.checked;
                                    setBiometricConsent(checked);
                                    if (!checked && showCamera) stopCamera();
                                }}
                                className="mt-1 w-4 h-4 rounded border-gray-700 bg-dk-800 text-azure-500 focus:ring-azure-500/40 cursor-pointer accent-azure-500 shrink-0"
                            />
                            <span className="text-[10px] text-gray-300 leading-relaxed font-medium">
                                I grant explicit, affirmative consent for transient facial and physical silhouette extraction to synthesize my styling digital twin. Raw images are held only in temporary buffer and purged immediately post-analysis; only synthesized styling parameters are retained in my encrypted account vault.
                            </span>
                        </label>
                    </div>
                )}

                <div className="flex flex-wrap justify-center gap-4 w-full max-w-sm">
                    {!preview && isMobile && (
                        <>
                            <label
                                onClick={(e) => {
                                    if (!biometricConsent) {
                                        e.preventDefault();
                                        showToast("Affirmative biometric consent required prior to taking a photo.", "error");
                                    }
                                }}
                                className={`flex-1 btn-azure py-4 text-[10px] flex items-center justify-center gap-3 transition-all shadow-lg ${!biometricConsent ? 'opacity-40 cursor-not-allowed filter grayscale' : 'cursor-pointer hover:scale-[1.01] active:scale-[0.99]'}`}
                            >
                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path d="M4 5a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V7a2 2 0 00-2-2h-1.586a1 1 0 01-.707-.293l-1.121-1.121A2 2 0 0011.172 3H8.828a2 2 0 00-1.414.586L6.293 4.707A1 1 0 015.586 5H4zm6 9a3 3 0 100-6 3 3 0 000 6z"></path></svg>
                                Take Photo
                                <input type="file" accept="image/*" capture="user" disabled={!biometricConsent} onChange={handleFileChange} className="hidden" />
                            </label>
                            <label
                                onClick={(e) => {
                                    if (!biometricConsent) {
                                        e.preventDefault();
                                        showToast("Affirmative biometric consent required prior to portrait upload.", "error");
                                    }
                                }}
                                className={`flex-1 bg-white/5 border text-white py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-3 transition-all ${!biometricConsent ? 'border-white/5 opacity-40 cursor-not-allowed filter grayscale' : 'border-white/10 hover:border-white/20 cursor-pointer'}`}
                            >
                                <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20"><path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z"></path></svg>
                                Upload Photo
                                <input type="file" accept="image/*" disabled={!biometricConsent} onChange={handleFileChange} className="hidden" />
                            </label>
                        </>
                    )}

                    {!preview && !isMobile && (
                        <>
                            <button
                                onClick={startCamera}
                                disabled={!biometricConsent}
                                className={`flex-1 btn-azure py-4 text-[10px] flex items-center justify-center gap-3 transition-all ${!biometricConsent ? 'opacity-40 cursor-not-allowed filter grayscale' : ''}`}
                            >
                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path d="M4 5a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V7a2 2 0 00-2-2h-1.586a1 1 0 01-.707-.293l-1.121-1.121A2 2 0 0011.172 3H8.828a2 2 0 00-1.414.586L6.293 4.707A1 1 0 015.586 5H4zm6 9a3 3 0 100-6 3 3 0 000 6z"></path></svg>
                                Use Webcam
                            </button>
                            <label
                                onClick={(e) => {
                                    if (!biometricConsent) {
                                        e.preventDefault();
                                        showToast("Affirmative biometric consent required prior to portrait upload.", "error");
                                    }
                                }}
                                className={`flex-1 bg-white/5 border text-white py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-3 transition-all ${!biometricConsent ? 'border-white/5 opacity-40 cursor-not-allowed filter grayscale' : 'border-white/10 hover:border-white/20 cursor-pointer'}`}
                            >
                                <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20"><path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z"></path></svg>
                                Upload Photo
                                <input type="file" accept="image/*" disabled={!biometricConsent} onChange={handleFileChange} className="hidden" />
                            </label>
                        </>
                    )}

                    {preview && (
                        <button onClick={handleInitialize} disabled={isProcessing || !biometricConsent} className="w-full btn-azure py-4 text-[10px] shadow-azure/40 animate-pulse-slow">
                            {isProcessing ? "Decoding Identity Markers..." : "Analyze DNA"}
                        </button>
                    )}
                </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-10 animate-fade-in">
            <header className="flex flex-col md:flex-row justify-between items-end gap-6 border-b border-white/5 pb-8">
                <div className="text-left">
                    <span className="text-gold text-[10px] font-black uppercase tracking-[0.5em] mb-4 block">
                        {isReadOnly ? "Identity Protocol: Locked State" : "Identity Protocol: Layer 02"}
                    </span>
                    <h2 className="text-2xl md:text-3xl font-black text-white italic uppercase tracking-tighter leading-none">
                        DNA <span className="text-azure-500">{isReadOnly ? "Profile" : "Calibration"}</span>
                    </h2>
                </div>
                <div className="flex gap-4">
                    <button onClick={handleRecalibrate} className="text-[10px] font-black text-gray-500 hover:text-white uppercase tracking-widest transition-colors border border-white/10 px-6 py-2 rounded-xl cursor-pointer">
                        Recalibrate
                    </button>
                    {!isReadOnly && (
                        <button onClick={handleFinalize} disabled={isProcessing} className="btn-azure text-[10px] py-2 px-8 shadow-azure/40 uppercase tracking-widest cursor-pointer">
                            {isProcessing ? 'Synthesizing...' : 'Lock Identity'}
                        </button>
                    )}
                </div>
            </header>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                {/* Left: Twin Model or Uploaded Reference */}
                <div className="flex flex-col gap-6">
                    <div className="relative group rounded-[2.5rem] overflow-hidden border-2 border-azure/20 shadow-2xl shadow-azure/10 aspect-[3/4] bg-dk-800">
                        {isProcessing && (
                            <div className="absolute inset-0 bg-dk-900/80 backdrop-blur-sm flex flex-col items-center justify-center gap-3 z-30">
                                <div className="w-8 h-8 border-2 border-azure border-t-transparent rounded-full animate-spin"></div>
                                <span className="text-[10px] font-black text-azure-400 uppercase tracking-widest text-center px-4">
                                    Synthesizing Digital Twin...
                                </span>
                            </div>
                        )}
                        {isReadOnly && twinUrl ? (
                            <img src={twinUrl} alt="Digital Twin" className="w-full h-full object-cover" />
                        ) : preview ? (
                            <img src={preview} alt="Reference Portrait" className="w-full h-full object-cover" />
                        ) : twinUrl ? (
                            <img src={twinUrl} alt="Digital Twin" className="w-full h-full object-cover" />
                        ) : (
                            <div className="flex items-center justify-center h-full animate-pulse">
                                <span className="text-[10px] font-black text-azure-400 uppercase tracking-[0.5em]">No Reference Image</span>
                            </div>
                        )}
                        <div className="absolute top-0 left-0 bg-azure text-white text-[8px] font-black uppercase tracking-widest px-4 py-2 rounded-br-2xl shadow-lg">
                            {isReadOnly ? "AI Generated Twin" : "Reference Portrait (Transient)"}
                        </div>
                    </div>
                    <div className="p-4 bg-white/5 rounded-2xl border border-white/5">
                        <p className="text-[9px] font-black text-gray-500 uppercase tracking-widest mb-2 flex items-center">
                            {isReadOnly ? "Locked DNA Blueprint" : "Estimated Archetype"} <DNAInfo type="bodyProportions" />
                        </p>
                        <p className="text-xs text-white leading-relaxed italic">{dna.description}</p>
                        {!isReadOnly && (
                            <p className="text-[9px] text-gray-400 mt-2 font-medium">
                                Review and calibrate the DNA parameters. Clicking &quot;Lock Identity&quot; will synthesize your Digital Twin avatar and permanently purge this photo.
                            </p>
                        )}
                        {isReadOnly && (
                            <p className="text-[9px] text-gray-500 mt-2 font-medium">
                                DNA parameters are locked for consistency. To update your profile, click Recalibrate to initiate a new cycle.
                            </p>
                        )}
                    </div>
                </div>

                {/* Right: DNA Form */}
                <div className="space-y-8 max-h-[50vh] lg:max-h-[600px] overflow-y-auto pr-2 lg:pr-4 custom-scrollbar">
                    <section className="space-y-6 pb-12 lg:pb-0">
                        <div className="space-y-4">
                            <h4 className="text-[10px] font-black text-azure-400 uppercase tracking-widest flex items-center">Demographics <DNAInfo type="gender" /></h4>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-1">
                                    <label className="text-[8px] font-black text-gray-600 uppercase">Gender Identity</label>
                                    <select 
                                        disabled={isReadOnly} 
                                        className={`w-full input-glass text-xs ${isReadOnly ? 'opacity-60 cursor-not-allowed' : ''}`} 
                                        value={dna.estimatedGender || "Male"} 
                                        onChange={(e) => setDna({...dna, estimatedGender: e.target.value as any})}
                                    >
                                        <option value="Male" className="bg-dk-800">Male</option>
                                        <option value="Female" className="bg-dk-800">Female</option>
                                        <option value="Non-binary" className="bg-dk-800">Non-binary</option>
                                        <option value="Other" className="bg-dk-800">Other</option>
                                    </select>
                                </div>
                                <div className="space-y-1">
                                    <label className="text-[8px] font-black text-gray-600 uppercase">Age (Years) <DNAInfo type="age" /></label>
                                    <input 
                                        disabled={isReadOnly} 
                                        type="number" 
                                        min="16" 
                                        max="100" 
                                        className={`w-full input-glass text-xs ${isReadOnly ? 'opacity-60 cursor-not-allowed' : ''}`} 
                                        value={dna.estimatedAge || 28} 
                                        onChange={(e) => setDna({...dna, estimatedAge: parseInt(e.target.value) || 28})} 
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="space-y-4 pt-4 border-t border-white/5">
                            <h4 className="text-[10px] font-black text-azure-400 uppercase tracking-widest flex items-center">Proportions <DNAInfo type="bodyProportions" /></h4>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-1">
                                    <label className="text-[8px] font-black text-gray-600 uppercase">Vertical</label>
                                    <select disabled={isReadOnly} className={`w-full input-glass text-xs ${isReadOnly ? 'opacity-60 cursor-not-allowed' : ''}`} value={dna.bodyProportions?.vertical} onChange={(e) => setDna({...dna, bodyProportions: {...dna.bodyProportions, vertical: e.target.value as any}})}>
                                        <option value="Balanced" className="bg-dk-800">Balanced</option>
                                        <option value="Short Torso" className="bg-dk-800">Short Torso</option>
                                        <option value="Long Torso" className="bg-dk-800">Long Torso</option>
                                    </select>
                                </div>
                                <div className="space-y-1">
                                    <label className="text-[8px] font-black text-gray-600 uppercase">Horizontal (Shape)</label>
                                    <select disabled={isReadOnly} className={`w-full input-glass text-xs ${isReadOnly ? 'opacity-60 cursor-not-allowed' : ''}`} value={dna.bodyProportions?.horizontal} onChange={(e) => setDna({...dna, bodyProportions: {...dna.bodyProportions, horizontal: e.target.value as any}})}>
                                        <option value="Hourglass" className="bg-dk-800">Hourglass</option>
                                        <option value="Inverted Triangle" className="bg-dk-800">Inverted Triangle</option>
                                        <option value="Rectangle" className="bg-dk-800">Rectangle</option>
                                        <option value="Triangle (Pear)" className="bg-dk-800">Triangle (Pear)</option>
                                        <option value="Round (Apple)" className="bg-dk-800">Round (Apple)</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-4 pt-4 border-t border-white/5">
                            <h4 className="text-[10px] font-black text-azure-400 uppercase tracking-widest flex items-center">Bone Structure <DNAInfo type="boneStructure" /></h4>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-1">
                                    <label className="text-[8px] font-black text-gray-600 uppercase">Frame Size</label>
                                    <select disabled={isReadOnly} className={`w-full input-glass text-xs ${isReadOnly ? 'opacity-60 cursor-not-allowed' : ''}`} value={dna.boneStructure?.frameSize} onChange={(e) => setDna({...dna, boneStructure: {...dna.boneStructure, frameSize: e.target.value as any}})}>
                                        <option value="Small" className="bg-dk-800">Small</option>
                                        <option value="Medium" className="bg-dk-800">Medium</option>
                                        <option value="Large" className="bg-dk-800">Large</option>
                                    </select>
                                </div>
                                <div className="space-y-1">
                                    <label className="text-[8px] font-black text-gray-600 uppercase">Type</label>
                                    <select disabled={isReadOnly} className={`w-full input-glass text-xs ${isReadOnly ? 'opacity-60 cursor-not-allowed' : ''}`} value={dna.boneStructure?.type} onChange={(e) => setDna({...dna, boneStructure: {...dna.boneStructure, type: e.target.value as any}})}>
                                        <option value="Yin (Rounded)" className="bg-dk-800">Yin (Rounded)</option>
                                        <option value="Yang (Angular)" className="bg-dk-800">Yang (Angular)</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-4 pt-4 border-t border-white/5">
                            <h4 className="text-[10px] font-black text-azure-400 uppercase tracking-widest flex items-center">Color DNA <DNAInfo type="skinUndertone" /></h4>
                            <div className="grid grid-cols-3 gap-4">
                                <div className="space-y-1">
                                    <label className="text-[8px] font-black text-gray-600 uppercase">Undertone</label>
                                    <select disabled={isReadOnly} className={`w-full input-glass text-xs px-2 ${isReadOnly ? 'opacity-60 cursor-not-allowed' : ''}`} value={dna.skinUndertone} onChange={(e) => setDna({...dna, skinUndertone: e.target.value as any})}>
                                        <option value="Cool" className="bg-dk-800">Cool</option>
                                        <option value="Warm" className="bg-dk-800">Warm</option>
                                        <option value="Neutral" className="bg-dk-800">Neutral</option>
                                        <option value="Olive" className="bg-dk-800">Olive</option>
                                    </select>
                                </div>
                                <div className="space-y-1">
                                    <label className="text-[8px] font-black text-gray-600 uppercase">Hair Lvl <DNAInfo type="hairColor" /></label>
                                    <input disabled={isReadOnly} type="number" min="1" max="10" className={`w-full input-glass text-xs ${isReadOnly ? 'opacity-60 cursor-not-allowed' : ''}`} value={dna.hairColor?.level} onChange={(e) => setDna({...dna, hairColor: {...dna.hairColor, level: parseInt(e.target.value)}})} />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-[8px] font-black text-gray-600 uppercase">Eye Color <DNAInfo type="eyeColor" /></label>
                                    <select disabled={isReadOnly} className={`w-full input-glass text-xs px-2 ${isReadOnly ? 'opacity-60 cursor-not-allowed' : ''}`} value={dna.eyeColor} onChange={(e) => setDna({...dna, eyeColor: e.target.value as any})}>
                                        <option value="Deep Brown/Black" className="bg-dk-800">Brown</option>
                                        <option value="Blue/Gray" className="bg-dk-800">Blue</option>
                                        <option value="Green" className="bg-dk-800">Green</option>
                                        <option value="Hazel/Amber" className="bg-dk-800">Hazel</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </section>
                </div>
            </div>
          </div>
        )}

        {/* Camera Overlay */}
        {showCamera && (
            <div className="fixed inset-0 z-[250] bg-dk-900 flex flex-col items-center justify-center p-4">
                <div className="glass-panel max-w-2xl w-full rounded-[2.5rem] overflow-hidden relative shadow-2xl">
                    <video ref={videoRef} autoPlay playsInline className="w-full aspect-square object-cover" />
                    <canvas ref={canvasRef} className="hidden" />
                    
                    <div className="absolute inset-x-0 bottom-12 flex justify-center gap-8">
                        <button onClick={stopCamera} className="w-14 h-14 bg-white/10 backdrop-blur-md rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-colors">
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                        </button>
                        <button onClick={capturePhoto} className="w-16 h-16 bg-azure rounded-full flex items-center justify-center text-white shadow-xl shadow-azure/40 hover:scale-110 active:scale-95 transition-all">
                            <div className="w-8 h-8 border-4 border-white rounded-full"></div>
                        </button>
                    </div>
                </div>
            </div>
        )}

        <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-azure/5 rounded-full blur-3xl opacity-30"></div>
      </div>
    </div>
  </div>
  );
};

export default IdentityVault;
